import type { Balances, Task, LeaveAccumulationMap } from '../types';
import { weekContainsYearEnd } from './dateUtils';

/**
 * Get leave codes for a specific year from the leave accumulation map
 */
export const getLeaveCodesForYear = (
  year: number,
  leaveAccumulationMap: LeaveAccumulationMap
): { pto_code: string; comp_code: string } => {
  if (leaveAccumulationMap && leaveAccumulationMap[year]) {
    return leaveAccumulationMap[year];
  }
  // Default fallback
  return { pto_code: 'PTO', comp_code: 'Comp' };
};

/**
 * Calculate totals for a timesheet
 * This includes row totals and leave accrual calculations
 */
export const calculateTimesheetTotals = (
  timesheetData: { [taskName: string]: number[] },
  allTasks: Task[],
  balances: Balances,
  weekStartDate: Date,
  leaveAccumulationMap: LeaveAccumulationMap,
  balanceType: 'week' | 'orig'
): Balances => {
  const updatedBalances = { ...balances };

  // Identify leave tasks
  const leaveTypes = allTasks
    .filter((task) => task.leave.BOOL)
    .reduce((acc, task) => {
      acc[task.name.S] = '';
      return acc;
    }, {} as Record<string, string>);

  // Initialize leave balances
  let billableTotal = 0;
  for (const leaveType in leaveTypes) {
    if (updatedBalances[leaveType]) {
      updatedBalances[leaveType][balanceType] = 0;
    }
  }

  // Calculate row totals and billable total
  for (const taskName in timesheetData) {
    const hours = timesheetData[taskName];
    const rowTotal = hours.reduce((sum, h) => sum + h, 0);

    if (!updatedBalances[taskName]) {
      updatedBalances[taskName] = {
        initial: 0,
        accrual: 0,
        total: 0,
        week: 0,
        orig: 0,
      };
    }

    updatedBalances[taskName][balanceType] = rowTotal;

    // Add to billable total if not a leave type
    if (!(taskName in leaveTypes)) {
      billableTotal += rowTotal;
    }
  }

  // Calculate leave accrual for the week
  const oldYear = weekStartDate.getFullYear();
  const newYear = oldYear + 1;

  // Track billable hours per year
  const billablePerYear: { [year: number]: number } = {
    [oldYear]: 0,
    [newYear]: 0,
  };

  // Calculate billable hours by day/year
  for (const taskName in timesheetData) {
    if (taskName in leaveTypes) continue;

    const hours = timesheetData[taskName];
    hours.forEach((dayHours, dayIndex) => {
      const dayDate = new Date(weekStartDate);
      dayDate.setDate(dayDate.getDate() + dayIndex);
      const dayYear = dayDate.getFullYear();
      billablePerYear[dayYear] = (billablePerYear[dayYear] || 0) + dayHours;
    });
  }

  // Get leave codes for both years
  const oldYearCodes = getLeaveCodesForYear(oldYear, leaveAccumulationMap);
  const newYearCodes = getLeaveCodesForYear(newYear, leaveAccumulationMap);

  const ptoOld = oldYearCodes.pto_code;
  const compOld = oldYearCodes.comp_code;
  const ptoNew = newYearCodes.pto_code;
  const compNew = newYearCodes.comp_code;

  // Check if week contains year-end
  const yearInfo = weekContainsYearEnd(weekStartDate);
  const weekHasNewYearDays = yearInfo.containsYearEnd;

  const oldYearBillable = billablePerYear[oldYear] || 0;
  const newYearBillable = billablePerYear[newYear] || 0;

  // Calculate PTO accrual
  if (billableTotal > 40) {
    // Weekly total > 40, PTO is 8 total - assign to new year if week contains new year days
    if (weekHasNewYearDays) {
      if (updatedBalances[ptoNew]) {
        if (balanceType === 'week') {
          updatedBalances[ptoNew].week_accrual = 8;
        } else {
          updatedBalances[ptoNew].orig_accrual = 8;
        }
      }
      if (updatedBalances[ptoOld]) {
        if (balanceType === 'week') {
          updatedBalances[ptoOld].week_accrual = 0;
        } else {
          updatedBalances[ptoOld].orig_accrual = 0;
        }
      }
    } else {
      if (updatedBalances[ptoOld]) {
        if (balanceType === 'week') {
          updatedBalances[ptoOld].week_accrual = 8;
        } else {
          updatedBalances[ptoOld].orig_accrual = 8;
        }
      }
      if (updatedBalances[ptoNew]) {
        if (balanceType === 'week') {
          updatedBalances[ptoNew].week_accrual = 0;
        } else {
          updatedBalances[ptoNew].orig_accrual = 0;
        }
      }
    }
  } else {
    // Weekly total <= 40, calculate PTO per year based on their hours
    if (oldYearBillable > 0 && updatedBalances[ptoOld]) {
      if (balanceType === 'week') {
        updatedBalances[ptoOld].week_accrual = oldYearBillable / 5.0;
      } else {
        updatedBalances[ptoOld].orig_accrual = oldYearBillable / 5.0;
      }
    } else if (updatedBalances[ptoOld]) {
      if (balanceType === 'week') {
        updatedBalances[ptoOld].week_accrual = 0;
      } else {
        updatedBalances[ptoOld].orig_accrual = 0;
      }
    }

    if (newYearBillable > 0 && updatedBalances[ptoNew]) {
      if (balanceType === 'week') {
        updatedBalances[ptoNew].week_accrual = newYearBillable / 5.0;
      } else {
        updatedBalances[ptoNew].orig_accrual = newYearBillable / 5.0;
      }
    } else if (updatedBalances[ptoNew]) {
      if (balanceType === 'week') {
        updatedBalances[ptoNew].week_accrual = 0;
      } else {
        updatedBalances[ptoNew].orig_accrual = 0;
      }
    }
  }

  // Calculate Comp time - only when weekly total > 40
  if (billableTotal > 40) {
    const totalComp = billableTotal - 40.0;
    // If week contains any days in new year, assign all comp to new year; otherwise to old year
    if (weekHasNewYearDays) {
      if (updatedBalances[compNew]) {
        if (balanceType === 'week') {
          updatedBalances[compNew].week_accrual = totalComp;
        } else {
          updatedBalances[compNew].orig_accrual = totalComp;
        }
      }
      if (updatedBalances[compOld]) {
        if (balanceType === 'week') {
          updatedBalances[compOld].week_accrual = 0;
        } else {
          updatedBalances[compOld].orig_accrual = 0;
        }
      }
    } else {
      if (updatedBalances[compOld]) {
        if (balanceType === 'week') {
          updatedBalances[compOld].week_accrual = totalComp;
        } else {
          updatedBalances[compOld].orig_accrual = totalComp;
        }
      }
      if (updatedBalances[compNew]) {
        if (balanceType === 'week') {
          updatedBalances[compNew].week_accrual = 0;
        } else {
          updatedBalances[compNew].orig_accrual = 0;
        }
      }
    }
  } else {
    // No comp when weekly <= 40
    if (updatedBalances[compOld]) {
      if (balanceType === 'week') {
        updatedBalances[compOld].week_accrual = 0;
      } else {
        updatedBalances[compOld].orig_accrual = 0;
      }
    }
    if (updatedBalances[compNew]) {
      if (balanceType === 'week') {
        updatedBalances[compNew].week_accrual = 0;
      } else {
        updatedBalances[compNew].orig_accrual = 0;
      }
    }
  }

  return updatedBalances;
};

/**
 * Calculate the display balance for a task
 */
export const calculateTaskBalance = (taskName: string, balances: Balances): number => {
  if (!balances[taskName]) return 0;

  const balance = balances[taskName];
  let taskBal =
    balance.initial + balance.accrual + balance.orig - balance.week - balance.total;

  // Add accrual difference for leave types
  if (balance.week_accrual !== undefined && balance.orig_accrual !== undefined) {
    taskBal += balance.week_accrual - balance.orig_accrual;
  }

  return Math.round(taskBal * 100) / 100;
};

/**
 * Convert timesheet hours from API format to number array
 */
export const parseTimesheetHours = (hours: { L: Array<{ N: string }> }): number[] => {
  return hours.L.map((h) => parseFloat(h.N) || 0);
};

/**
 * Convert timesheet hours from number array to API format
 */
export const formatTimesheetHours = (hours: number[]): string[] => {
  return hours.map((h) => h.toString());
};

/**
 * Calculate total hours for a single task (row)
 */
export const calculateTaskTotal = (hours: string[]): number => {
  return hours.reduce((sum, hour) => {
    const num = parseFloat(hour);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);
};

/**
 * Calculate total hours for the entire week across all tasks
 */
export const calculateWeekTotal = (timesheetHours: { [taskName: string]: string[] }): number => {
  let total = 0;
  Object.values(timesheetHours).forEach((hours) => {
    total += calculateTaskTotal(hours);
  });
  return total;
};
